import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from "@mui/icons-material/Person";
import PublicIcon from "@mui/icons-material/Public";
import RadioGroup from "designSystem/components/radio/RadioGroup";
import { FlexColumn } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent } from "react";
import { MultiSelect, TextField } from "src/designSystem/index";
import { useCustomViewDialogStore } from "../CustomViewDialogStore";
import CheckboxOption from "../options/CheckboxOption";
import { ShareViewWith, ShareViewWithSubType } from "../options/CustomViewOptions";

const ShareWithRolesContent: FunctionComponent = observer(() => {
	const {
		customViewNodeContexts: {
			hasMultiple,
		},
		settingStore: {
			all,
			enableSpecificRolesSetViewAsDefault,
			everyoneTypeRadioLabel,
			selectedSpecificRoles,
			selectedSpecificRolesSetViewDefaultSpecificRoles,
			setSelectedSpecificRoles,
			setShareWithSubtypedDefault,
			shareWith,
			shareWithSubtypedDefault,
			specific,
			specificRoles,
			specificRolesSetViewAsDefault,
			specificSubTypeOptions,
			toggleSpecificRolesSetViewAsDefault,
			updateSelectedSpecificRolesSetViewDefaultSpecificRoles,
		},
	} = useCustomViewDialogStore();

	const shareWithSubtypedOptions = [
		{
			name: all,
			labelIcon: <PublicIcon />,
			value: ShareViewWithSubType.ALL,
		},
		{
			name: specific,
			labelContent: <SpecificRolesSpecificSubTypeOptions />,
			labelIcon: <PeopleIcon />,
			value: ShareViewWithSubType.SPECIFIC,
		},
	];

	return (
		<FlexColumn>
			<MultiSelect
				hidden={shareWith !== ShareViewWith.ROLES}
				noChips
				onChange={setSelectedSpecificRoles}
				options={specificRoles}
				placeHolderRes="CustomViewSettingSelectRoles"
				popperFitContentWidth
				value={selectedSpecificRoles}
			/>
			<CheckboxOption
				disabled={!enableSpecificRolesSetViewAsDefault}
				hidden={shareWith !== ShareViewWith.ROLES}
				label="CustomViewSettingSetViewAsDefault"
				onChange={toggleSpecificRolesSetViewAsDefault}
				optionValue={specificRolesSetViewAsDefault}
				paddingLeft={0}
			/>
			{specificRolesSetViewAsDefault && (
				<MultiSelect
					fullWidth
					labelRes="CustomViewSettingForTheseRoles"
					noChips
					onChange={updateSelectedSpecificRolesSetViewDefaultSpecificRoles}
					options={selectedSpecificRoles}
					placeHolderRes="CustomViewSettingSelectRoles"
					popperFitContentWidth
					value={selectedSpecificRolesSetViewDefaultSpecificRoles}
				/>
			)}
			{/* subtyped view section */}
			{specificRolesSetViewAsDefault && hasMultiple && (specificSubTypeOptions?.length > 1) && (
				<RadioGroup
					aria-label="shareWithSubtypedDefault"
					name={everyoneTypeRadioLabel}
					onChange={setShareWithSubtypedDefault}
					options={shareWithSubtypedOptions}
					value={shareWithSubtypedDefault}
				/>
			)}
		</FlexColumn>
	);
});

const EveryoneDefaultViewSubtypedSpecificRoles: FunctionComponent = observer(() => {
	const {
		settingStore: {
			everyoneShareWithSubtypedDefault,
			selectedEveryoneSpecificSubType,
			setEveryoneSpecificSubType,
			specificSubTypeOptions,
		},
	} = useCustomViewDialogStore();

	return (
		<FlexColumn>
			<MultiSelect
				hidden={everyoneShareWithSubtypedDefault === ShareViewWithSubType.ALL}
				noChips
				onChange={setEveryoneSpecificSubType}
				options={specificSubTypeOptions}
				placeHolderRes="CustomViewSettingSelectSubType"
				popperFitContentWidth
				value={selectedEveryoneSpecificSubType}
			/>
		</FlexColumn>
	);
});

const EveryoneDefaultViewSpecificRoles: FunctionComponent = observer(() => {
	const {
		settingStore: {
			everyoneShareWithDefault,
			specificRoles,
			selectedEveryoneSetViewDefaultSpecificRoles,
			setSelectedEveryoneSetViewDefaultSpecificRoles,
		},
	} = useCustomViewDialogStore();

	return (
		<FlexColumn>
			<MultiSelect
				hidden={everyoneShareWithDefault !== ShareViewWith.ROLES}
				noChips
				onChange={setSelectedEveryoneSetViewDefaultSpecificRoles}
				options={specificRoles}
				placeHolderRes="CustomViewSettingSelectRoles"
				popperFitContentWidth
				value={selectedEveryoneSetViewDefaultSpecificRoles}
			/>
		</FlexColumn>
	);
});

const SpecificRolesSpecificSubTypeOptions: FunctionComponent = observer(() => {
	const {
		settingStore: {
			selectedSpecificSubType,
			setSelectedSpecificSubType,
			shareWithSubtypedDefault,
			specificSubTypeOptions,
		},
	} = useCustomViewDialogStore();

	return (
		<FlexColumn>
			<MultiSelect
				hidden={shareWithSubtypedDefault === ShareViewWithSubType.ALL}
				noChips
				onChange={setSelectedSpecificSubType}
				options={specificSubTypeOptions}
				placeHolderRes="CustomViewSettingSelectSubType"
				popperFitContentWidth
				value={selectedSpecificSubType}
			/>
		</FlexColumn>
	);
});

const EveryoneViewDefault: FunctionComponent = observer(() => {
	const { resource } = useLocale();

	const {
		customViewNodeContexts: {
			hasMultiple,
		},
	} = useCustomViewDialogStore();

	const {
		settingStore: {
			all,
			canSelectEveryone,
			canSelectRoles,
			disableSetAsDefault,
			everyoneSetViewAsDefault,
			everyoneShareWithDefault,
			everyoneShareWithSubtypedDefault,
			everyoneTypeRadioLabel,
			selectedCustomViewIsOOTB,
			setEveryoneShareWithDefault,
			setEveryoneShareWithSubtypedDefault,
			shareWith,
			specific,
			specificSubTypeOptions,
			toggleEveryoneSetViewAsDefault,
		},
	} = useCustomViewDialogStore();

	const everyoneShareWithOptions = [
		{
			disabled: !canSelectEveryone,
			name: resource("CustomViewSettingEveryone"),
			labelIcon: <PublicIcon />,
			value: ShareViewWith.EVERYONE,
		},
		{
			disabled: !canSelectRoles,
			name: resource("CustomViewSettingSpecificRoles"),
			labelContent: <EveryoneDefaultViewSpecificRoles />,
			labelIcon: <PeopleIcon />,
			value: ShareViewWith.ROLES,
		},
	];

	const everyoneShareWithSubtypedOptions = [
		{
			disabled: selectedCustomViewIsOOTB,
			name: all,
			labelIcon: <PublicIcon />,
			value: ShareViewWithSubType.ALL,
		},
		{
			disabled: selectedCustomViewIsOOTB,
			name: specific,
			labelContent: <EveryoneDefaultViewSubtypedSpecificRoles />,
			labelIcon: <PeopleIcon />,
			value: ShareViewWithSubType.SPECIFIC,
		},
	];

	return (shareWith === ShareViewWith.EVERYONE) && (
		<FlexColumn>
			<CheckboxOption
				disabled={disableSetAsDefault}
				label="CustomViewSettingSetViewAsDefault"
				onChange={toggleEveryoneSetViewAsDefault}
				optionValue={everyoneSetViewAsDefault}
				paddingLeft={0}
			/>
			{everyoneSetViewAsDefault && (
				<RadioGroup
					aria-label="everyoneShareWithDefault"
					name={resource("CustomViewSettingForTheseUsers")}
					onChange={setEveryoneShareWithDefault}
					options={everyoneShareWithOptions}
					value={everyoneShareWithDefault}
				/>
			)}
			{/* subtyped view section */}
			{everyoneSetViewAsDefault && hasMultiple && (specificSubTypeOptions?.length > 1) && (
				<RadioGroup
					aria-label="everyoneShareWithSubtypedDefault"
					name={everyoneTypeRadioLabel}
					onChange={setEveryoneShareWithSubtypedDefault}
					options={everyoneShareWithSubtypedOptions}
					value={everyoneShareWithSubtypedDefault}
				/>
			)}
		</FlexColumn>
	);
});

const SettingsTab: FunctionComponent = observer(() => {
	const { resource } = useLocale();

	const {
		STANDARD_PADDING,
	} = useCustomViewDialogStore();

	const {
		settingStore: {
			activeDescription,
			activeName,
			canSelectEveryone,
			canSelectMe,
			canSelectRoles,
			forceShareWithEveryone,
			nameErrorRes,
			onBlurDescription,
			onBlurName,
			setActiveDescription,
			setActiveName,
			setShareWith,
			shareWith,
		},
	} = useCustomViewDialogStore();

	const shareWithOptions = [
		{
			disabled: !canSelectEveryone,
			name: resource("CustomViewSettingEveryone"),
			labelContent: <EveryoneViewDefault />,
			labelIcon: <PublicIcon />,
			value: ShareViewWith.EVERYONE,
		},
		{
			disabled: !canSelectRoles,
			name: resource("CustomViewSettingSpecificRoles"),
			labelContent: <ShareWithRolesContent />,
			labelIcon: <PeopleIcon />,
			value: ShareViewWith.ROLES,
		},
		{
			disabled: !canSelectMe,
			name: resource("CustomViewSettingJustMe"),
			labelIcon: <PersonIcon />,
			value: ShareViewWith.ME,
		},
	];

	return (
		<FlexColumn 
			data-csi-automation="SettingsTab"
			gap={2} 
			padding={STANDARD_PADDING}
		>
			<TextField
				error={!!nameErrorRes}
				fullWidth
				helperTextRes={nameErrorRes}
				onBlur={onBlurName}
				onChange={setActiveName}
				res="CustomViewSettingViewName"
				value={activeName}
			/>
			<TextField
				fullWidth
				multiline
				onBlur={onBlurDescription}
				onChange={setActiveDescription}
				res="Description"
				value={activeDescription}
			/>
			{!forceShareWithEveryone && (
				<RadioGroup
					aria-label="shareWith"
					name={resource("CustomViewSettingShareViewWith")}
					onChange={setShareWith}
					options={shareWithOptions}
					value={shareWith}
				/>
			)}
		</FlexColumn>
	);
});

export default SettingsTab;